import { Permission, PermissionValue } from "../../auth/permissions";

// "placeholder" = not built yet: shown disabled with "Coming soon" (SHOW_PLACEHOLDERS). Flip an
// item to "active" when its page ships.
export type MenuItemStatus = "active" | "placeholder";

/** Show not-yet-built items in the menu, disabled with a "Coming soon" tooltip. */
export const SHOW_PLACEHOLDERS = true;

export type MenuItem = {
  label: string;
  route: string;
  permission: PermissionValue;
  status: MenuItemStatus;
  icon: string; // key in menuIconMap (menu/menuIcons.tsx)
};

export type MenuGroup = {
  label: string;
  icon: string; // key in menuIconMap (menu/menuIcons.tsx)
  items: MenuItem[];
  // Shown as one direct link (its single item) instead of an expandable group, e.g. Settings,
  // whose sections are navigated inside the page
  link?: boolean;
};

// Dashboard is intentionally not here — every logged-in role sees it, rendered by AppSidebar.
export const menuConfig: MenuGroup[] = [
  {
    // The user's own pages (faculty now; students get My Leave later). Admin doesn't see it: the
    // *_OWN permissions are excluded from admin (rolePermissions.ts).
    label: "My Space",
    icon: "my_space",
    items: [
      { label: "My Timetable", route: "/app/my-timetable", permission: Permission.TIMETABLE_VIEW_OWN, status: "active", icon: "timetable" },
      { label: "My Leave", route: "/app/my-leave", permission: Permission.LEAVE_APPLY_OWN, status: "active", icon: "leave" },
      // classes this faculty covers for a colleague on leave
      { label: "My Substitutions", route: "/app/my-substitutions", permission: Permission.SUBSTITUTIONS_VIEW_OWN, status: "active", icon: "substitutions" },
    ],
  },
  {
    label: "Enquiries",
    icon: "enquiries",
    items: [
      { label: "Directory", route: "/app/enquiries", permission: Permission.STUDENTS_MANAGE, status: "active", icon: "list" },
    ],
  },
  {
    label: "Students",
    icon: "students",
    items: [
      { label: "Import", route: "/app/students/import", permission: Permission.STUDENTS_MANAGE, status: "active", icon: "import" },
      { label: "Directory", route: "/app/students", permission: Permission.STUDENTS_MANAGE, status: "active", icon: "list" },
      { label: "Transfer Cert.", route: "/app/students/tc", permission: Permission.STUDENTS_MANAGE, status: "active", icon: "transfer_certificate" },
      // Fathers, mothers, guardians and emergency contacts; Add is on the page
      { label: "Guardians", route: "/app/students/guardians", permission: Permission.PERSONS_MANAGE, status: "active", icon: "guardians" },
    ],
  },
  {
    label: "Finance",
    icon: "finance",
    items: [
      // Generic — no fee-category concept exists yet (see FeeDue type), so this covers whatever fees
      // exist today. Fee Structures is where Tuition/Exam/Library/Transport fee types will be defined.
      { label: "Outstanding", route: "/app/fees/outstanding", permission: Permission.FEES_VIEW, status: "active", icon: "outstanding" },
      { label: "Receipts", route: "/app/fees/receipts", permission: Permission.FEES_RECEIPTS_VIEW, status: "active", icon: "receipts" },
      { label: "Payments", route: "/app/fees/payments", permission: Permission.FEES_PAYMENTS_VIEW_OWN, status: "active", icon: "payments" },
      { label: "Fee Structures", route: "/app/fees/structures", permission: Permission.CONFIG_FEE_STRUCTURE_MANAGE, status: "active", icon: "fee_structure" },
    ],
  },
  {
    label: "Attendance",
    icon: "attendance",
    items: [
      { label: "Students", route: "/app/attendance/students", permission: Permission.STUDENTS_ATTENDANCE_MARK, status: "active", icon: "attendance_students" },
      // Covers both Faculty and Support Staff — only one real endpoint exists today.
      { label: "Staff", route: "/app/attendance/staff", permission: Permission.STAFF_ATTENDANCE_MARK, status: "active", icon: "attendance_staff" },
    ],
  },
  {
    // What the school teaches. Setup = one page with tabs, in setup order: Academic Years, Sections,
    // Grades, Subjects, Class Subjects. Admin only.
    label: "Academics",
    icon: "academics",
    items: [
      { label: "Setup", route: "/app/academics/setup", permission: Permission.CONFIG_ACADEMICS_MANAGE, status: "active", icon: "setup" },
      { label: "Timetable", route: "/app/academics/timetable", permission: Permission.TIMETABLE_MANAGE, status: "active", icon: "timetable" },
      { label: "Year Transition", route: "/app/academics/year-transition", permission: Permission.CONFIG_ACADEMICS_MANAGE, status: "active", icon: "year_transition" },
    ],
  },
  {
    // Faculty and support staff in one place: the Directory page switches between them
    // (Faculty by default) and has the Add button, so there's no separate Add item.
    label: "Staff",
    icon: "staff",
    items: [
      { label: "Directory", route: "/app/staff", permission: Permission.STAFF_MANAGE, status: "active", icon: "list" },
      // faculty leave: approve / reject (students' leave later under Students, same page)
      { label: "Leave Requests", route: "/app/staff/leave", permission: Permission.LEAVE_MANAGE, status: "active", icon: "leave" },
      { label: "Substitutions", route: "/app/staff/substitutions", permission: Permission.SUBSTITUTIONS_MANAGE, status: "active", icon: "substitutions" },
    ],
  },
  {
    label: "Transport",
    icon: "transport",
    items: [
      { label: "Routes & Schedule", route: "/app/transport/routes", permission: Permission.TRANSPORT_MANAGE, status: "placeholder", icon: "routes_schedule" },
      { label: "Assignments", route: "/app/transport/assignments", permission: Permission.TRANSPORT_MANAGE, status: "placeholder", icon: "assignment" },
    ],
  },
  {
    label: "Library",
    icon: "library",
    items: [
      { label: "Catalog", route: "/app/library/catalog", permission: Permission.LIBRARY_MANAGE, status: "placeholder", icon: "catalog" },
      { label: "Assignments", route: "/app/library/assignments", permission: Permission.LIBRARY_MANAGE, status: "placeholder", icon: "assignment" },
    ],
  },
  {
    label: "Settings",
    icon: "settings",
    link: true,
    // School Profile, Fees & Receipts, System, ... are sections inside the page (features/settings)
    items: [{ label: "Settings", route: "/app/settings", permission: Permission.CONFIG_SCHOOL_MANAGE, status: "active", icon: "settings" }],
  },
  {
    label: "User & Access",
    icon: "users",
    items: [
      { label: "Add", route: "/app/users/new", permission: Permission.USERS_MANAGE, status: "active", icon: "add" },
      { label: "Directory", route: "/app/users", permission: Permission.USERS_MANAGE, status: "active", icon: "list" },
    ],
  },
  {
    // one page for every kind of notification; the Type picks a DLT template (Holiday, Event, Fee reminder, ...)
    label: "Notifications",
    icon: "notifications",
    items: [
      { label: "Send", route: "/app/notifications/send", permission: Permission.ALERTS_MANAGE, status: "active", icon: "send" },
    ],
  },
];
